import React, { useEffect, useRef, useState } from "react";
import { ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from "recharts";
import { ChevronDown } from "lucide-react";

export const YEAR_COLORS = ["#1C64B0", "#059669", "#d97706", "#dc2626", "#7c3aed", "#0891b2", "#ca8a04", "#db2777", "#4f46e5", "#16a34a", "#ea580c"];
export const inp = "mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm placeholder-slate-400 focus:border-[#1C64B0] focus:ring-1 focus:ring-[#1C64B0] focus:outline-none transition-shadow disabled:bg-slate-50 disabled:text-slate-400";

export const Card = ({ children, className = "", ...rest }) => <div className={`rounded-xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition-shadow hover:shadow-[0_4px_14px_rgba(15,23,42,0.05)] ${className}`} {...rest}>{children}</div>;

export const Kpi = ({ label, value, status, sub }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition-shadow hover:shadow-[0_4px_14px_rgba(15,23,42,0.05)]" data-testid={`kpi-${label}`}>
    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{label}</div>
    <div className={`mt-2 text-2xl font-bold tracking-tight tabular-nums font-heading ${status === "good" ? "text-emerald-600" : status === "bad" ? "text-rose-600" : "text-slate-900"}`}>{value}</div>
    {sub && <div className={`mt-1 text-xs font-semibold ${status === "good" ? "text-emerald-600" : status === "bad" ? "text-rose-600" : "text-slate-400"}`}>{sub}</div>}
  </div>
);

export function Field({ label, children }) {
  return <label className="block"><span className="text-xs font-semibold text-slate-600">{label}</span>{children}</label>;
}

export function NumberInput({ value, onChange, placeholder, testid }) {
  return <input type="number" data-testid={testid} className={inp} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />;
}

export function SelectInput({ value, onChange, options, testid }) {
  return (
    <select data-testid={testid} className={inp} value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o, i) => { const val = typeof o === "object" ? o.value : o; const lbl = typeof o === "object" ? o.label : o; return <option key={`${val}-${i}`} value={val}>{lbl || "-"}</option>; })}
    </select>
  );
}

export function ProductPicker({ products, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => { const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }; document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h); }, []);
  const sel = products.find((p) => p.naam === value);
  const label = sel ? `${sel.artikelnummer ? sel.artikelnummer + " - " : ""}${sel.naam}` : "";
  const ql = q.trim().toLowerCase();
  const filtered = (ql ? products.filter((p) => `${p.artikelnummer || ""} ${p.naam}`.toLowerCase().includes(ql)) : products).slice(0, 60);
  return (
    <div className="relative" ref={ref}>
      <input data-testid={testid} className={inp} value={open ? q : label} placeholder="Zoek op artikelnummer of naam…" onFocus={() => { setOpen(true); setQ(""); }} onChange={(e) => { setQ(e.target.value); setOpen(true); }} />
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {filtered.length === 0 ? <div className="px-3 py-2 text-sm text-slate-400">Geen artikel gevonden</div>
            : filtered.map((p) => (
              <button type="button" key={p.id} data-testid="invoer-product-option" onClick={() => { onChange(p.naam); setOpen(false); }} className="block w-full text-left px-3 py-2 text-sm hover:bg-blue-50">
                <span className="font-semibold text-[#1C64B0]">{p.artikelnummer || "—"}</span> · {p.naam}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

export function Table({
  columns,
  rows,
  testid,
  hideCols = [],
  rowClass,
  rowKeys,
  tableClassName = "",
  cellClassName = "",
  stickyLastCol = false,
}) {
  const keep = (arr) => (hideCols.length ? arr.filter((_, i) => !hideCols.includes(i)) : arr);
  const cols = keep(columns);
  const handleKeyNav = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const el = e.target;
    if (el.tagName !== "INPUT") return;
    const type = (el.getAttribute("type") || "text").toLowerCase();
    if (type === "checkbox" || type === "radio" || type === "range" || el.hasAttribute("list")) return;
    const td = el.closest("td");
    const tr = el.closest("tr");
    if (td && tr) {
      const cellIndex = Array.prototype.indexOf.call(tr.children, td);
      const targetRow = e.key === "ArrowDown" ? tr.nextElementSibling : tr.previousElementSibling;
      const targetCell = targetRow && targetRow.children[cellIndex];
      const focusable = targetCell && targetCell.querySelector("input, select, textarea");
      if (focusable) {
        e.preventDefault();
        focusable.focus();
        if (typeof focusable.select === "function") { try { focusable.select(); } catch (_) { /* noop */ } }
        return;
      }
    }
    // Geen volgende/vorige rij: voorkom in elk geval dat een number-veld op- of afgeteld wordt.
    if (type === "number") e.preventDefault();
  };
  return (
    <div onKeyDown={handleKeyNav} className="rounded-xl border border-slate-200 bg-white overflow-x-auto shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
      <table className={`w-full text-sm ${tableClassName}`}>
        <thead><tr className="bg-slate-50 border-b border-slate-200">{cols.map((c, ci) => <th key={c} className={`text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap ${stickyLastCol && ci === cols.length - 1 ? "sticky right-0 bg-slate-50 z-10 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.12)]" : ""}`}>{c}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={cols.length} className="text-center text-slate-400 py-8">Geen gegevens</td></tr>
            : rows.map((row, i) => { const kept = keep(row); return <tr key={rowKeys && rowKeys[i] != null ? rowKeys[i] : i} className={`group border-t border-slate-100 transition-colors hover:bg-slate-100 focus-within:bg-blue-50 ${rowClass ? rowClass(i) : ""}`} data-testid={testid}>{kept.map((cell, j) => <td key={j} className={`px-4 py-2.5 whitespace-nowrap tabular-nums text-slate-700 ${cellClassName} ${stickyLastCol && j === kept.length - 1 ? "sticky right-0 bg-white group-hover:bg-slate-100 group-focus-within:bg-blue-50 z-10 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.12)]" : ""}`}>{cell}</td>)}</tr>; })}
        </tbody>
      </table>
    </div>
  );
}

// Herbruikbaar inklap-kaart: klik op de titelbalk om het gedeelte dicht/open te klappen.
// Onthoudt de stand per `storageKey` (localStorage), zodat het ingeklapt blijft na herladen.
export function CollapsibleCard({ title, subtitle, right, children, defaultOpen = true, storageKey, testid, className = "" }) {
  const key = storageKey ? `clazing_collapse_${storageKey}` : null;
  const [open, setOpen] = useState(() => {
    if (!key) return defaultOpen;
    try { const v = localStorage.getItem(key); return v === null ? defaultOpen : v === "1"; } catch (_) { return defaultOpen; }
  });
  const toggle = () => setOpen((o) => { const n = !o; if (key) { try { localStorage.setItem(key, n ? "1" : "0"); } catch (_) { /* noop */ } } return n; });
  return (
    <div data-testid={testid} className={`rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition-shadow hover:shadow-[0_4px_14px_rgba(15,23,42,0.05)] ${className}`}>
      <div className="flex items-center gap-2 px-5 py-3.5">
        <button type="button" data-testid={testid ? `${testid}-toggle` : undefined} onClick={toggle} aria-expanded={open} className="group flex min-w-0 flex-1 items-center gap-2 text-left">
          <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform group-hover:text-slate-600 ${open ? "" : "-rotate-90"}`} />
          <span className="min-w-0">
            <span className="block truncate text-sm font-bold text-slate-800">{title}</span>
            {subtitle && <span className="block truncate text-xs text-slate-400">{subtitle}</span>}
          </span>
        </button>
        {right && <div className="shrink-0">{right}</div>}
      </div>
      {open && <div className="px-5 pb-5 pt-0">{children}</div>}
    </div>
  );
}

export function MultiLineChart({ data, years, height = 260 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#64748b" }} />
        <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
        <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
        {years.map((y, i) => <Line key={y} type="monotone" dataKey={String(y)} stroke={YEAR_COLORS[i % YEAR_COLORS.length]} strokeWidth={2} dot={false} connectNulls />)}
      </LineChart>
    </ResponsiveContainer>
  );
}
